import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Grid } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import styled from 'styled-components';
import { ExposureConfigurationResponse } from '../../../api/ConfigApi';
import { AuthData } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { EndorsementExposureUpdateRequest } from '../../../types/endorsements';
import {
	Exposure,
	ExposureCharacteristics,
	PolicyCharacteristics,
} from '../../../types/policy';
import { RenewalExposureUpdateRequest } from '../../../types/renewals';
import FieldLabel from '../../fields/FieldLabel';
import AddedPerils from './AddedPerils';
import FieldGroupChanges from './FieldGroupChanges';
import RemovedPerils from './RemovedPerils';
import UpdatedPerils from './UpdatedPerils';

interface Props {
	authData: AuthData;
	characteristicTimestamp: string;
	referenceTimestamp: string;
	exposure: Exposure;
	exposureCharacteristic: ExposureCharacteristics;
	updatedExposure:
		| EndorsementExposureUpdateRequest
		| RenewalExposureUpdateRequest;
	exposureConfiguration: ExposureConfigurationResponse;
	policyCharacteristic: PolicyCharacteristics;
	policyData: PolicyData;
}

const useStyles = makeStyles((theme) => ({
	disabledInput: {
		'& .MuiInputBase-root.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
	},
}));

const UpdatedExposureDetails = (props: Props) => {
	const classes = useStyles();
	const {
		characteristicTimestamp,
		referenceTimestamp,
		exposure,
		exposureCharacteristic,
		updatedExposure,
		exposureConfiguration,
		policyCharacteristic,
		policyData,
		authData,
	} = props;
	const { policy } = policyData;

	const changedFieldValues = updatedExposure.fieldValues || {};
	const fieldConfigurations = exposureConfiguration.fields;

	const renderField = (field: any, index: number) => {
		const value: any = changedFieldValues[field.name] || '';
		return (
			<FormControl>
				<FieldLabel required={!field.optional} title={field.title} />
				<TextField
					className={classes.disabledInput}
					id={`${field.name}-${index}`}
					value={value}
					disabled
				/>
			</FormControl>
		);
	};

	const renderExposureFieldChanges = () => {
		const changedValues = Object.keys(changedFieldValues).map(
			(fieldName: string, index: number) => {
				const fieldConfig = fieldConfigurations.find(
					(f) => f.name === fieldName,
				);

				const key1 = `${fieldName}-${index}`;

				return (
					<Grid item key={key1}>
						{fieldConfig.newline && <Break />}
						{renderField(fieldConfig, index)}
					</Grid>
				);
			},
		);
		if (!changedValues || !changedValues.length) {
			return null;
		}
		return (
			<div style={{ marginTop: '10px ', marginBottom: '10px' }}>
				<Grid container spacing={1}>
					{changedValues}
				</Grid>
			</div>
		);
	};

	const renderAddedPerils = () => {
		if (
			!updatedExposure ||
			!updatedExposure.addPerils ||
			!updatedExposure.addPerils.length
		) {
			return null;
		}
		return (
			<AddedPerils
				addedPerils={updatedExposure.addPerils}
				exposureConfiguration={exposureConfiguration}
				policyCharacteristic={policyCharacteristic}
				currency={policy?.currency}
				authData={authData}
			/>
		);
	};

	const renderUpdatedPerils = () => {
		if (
			!updatedExposure ||
			!updatedExposure.updatePerils ||
			!updatedExposure.updatePerils.length
		) {
			return null;
		}
		return (
			<UpdatedPerils
				perils={exposure.perils}
				updatedPerils={updatedExposure.updatePerils}
				exposureConfiguration={exposureConfiguration}
				policyCharacteristic={policyCharacteristic}
				referenceTimestamp={referenceTimestamp}
				characteristicTimestamp={characteristicTimestamp}
			/>
		);
	};

	const renderRemovedPerils = () => {
		if (
			!updatedExposure ||
			!updatedExposure.endPerils ||
			!updatedExposure.endPerils.length
		) {
			return null;
		}
		return (
			<RemovedPerils
				perils={exposure.perils}
				characteristicTimestamp={characteristicTimestamp}
				referenceTimestamp={referenceTimestamp}
				removedPerils={updatedExposure.endPerils}
				exposureConfiguration={exposureConfiguration}
			/>
		);
	};

	const exposureName = exposureConfiguration.displayName
		? exposureConfiguration.displayName
		: exposure.name;
	return (
		<Accordion>
			<AccordionSummary
				style={{ borderBottom: '1px solid rgba(0, 0, 0, .125)' }}
				expandIcon={<ExpandMoreIcon />}
			>{`Updated: ${exposureName} ${exposure.displayId}`}</AccordionSummary>
			<AccordionDetails style={{ display: 'flex', flexDirection: 'column' }}>
				{renderExposureFieldChanges()}
				<FieldGroupChanges
					originalFieldGroups={exposureCharacteristic.fieldGroupsByLocator}
					originalFieldValues={exposureCharacteristic.fieldValues}
					addedFieldGroups={updatedExposure.addFieldGroups}
					removedFieldGroups={updatedExposure.removeFieldGroups}
					updatedFieldGroups={updatedExposure.updateFieldGroups}
					fieldConfigurations={fieldConfigurations}
				/>
				{renderUpdatedPerils()}
				{renderAddedPerils()}
				{renderRemovedPerils()}
			</AccordionDetails>
		</Accordion>
	);
};

const Break = styled.div`
	width: 100%;
	height: 0;
`;

export default withPolicyContext<Props>(UpdatedExposureDetails);
